/**
 * normalOperatingDays data is not in the ideal format that we need for rendering UI
 * so we convert normalOperatingDays -> normalOperatingDaysRows to use in APP
 * when we're about to patch schedule changes,
 * we convert normalOperatingDaysRows -> normalOperatingDays for API
 *
 * same with specialOperatingDays
 */

import Immutable from "immutable";

const DEFAULT_START_TIME = "00:00";
const DEFAULT_END_TIME = "23:59";

/**
 * @param {String} startTime
 * @param {String} endTime
 * @returns {Object}
 */
function getTimeInfo(startTime, endTime) {
  const allDay = startTime === DEFAULT_START_TIME && endTime === DEFAULT_END_TIME;
  const start = allDay ? null : startTime;
  const end = allDay ? null : endTime;

  return { allDay, start, end };
}

/**
 * @param {Immutable.List} row - a row from normalOperatingDaysRows or specialOperatingDaysRows
 * @returns {Object}
 */
function getStartEndTimes(row) {
  const start = row.get("allDay") ? DEFAULT_START_TIME : row.get("start");
  const end = row.get("allDay") ? DEFAULT_END_TIME : row.get("end");

  return { start, end };
}

/**
 * convert normalOperatingDays to normalOperatingDaysRows
 * @param {Immutable.Map} normalOperatingDays
 * @returns {Immutable.List}
 */
export function getNormalOperatingDaysRows(normalOperatingDays) {
  let normalOperatingDaysRows = Immutable.List();

  normalOperatingDays.forEach((timeRanges, day) => {
    if (timeRanges) {
      timeRanges.forEach((timeRange) => {
        const timeInfo = getTimeInfo(
          timeRange.get("start"),
          timeRange.get("end")
        );
        const { allDay, start, end } = timeInfo;

        const existingRowIndex = normalOperatingDaysRows.findIndex(
          row => row.get("start") === start && row.get("end") === end
        );

        if (existingRowIndex > -1) {
          normalOperatingDaysRows = normalOperatingDaysRows.updateIn([existingRowIndex, "days"], days => days.push(day));
        } else {
          normalOperatingDaysRows = normalOperatingDaysRows.push(Immutable.Map({
            date: null,
            days: Immutable.List([day]),
            start,
            end,
            allDay,
            invalidFields: Immutable.List(),
            conflictingDays: Immutable.Map({
              days: Immutable.Set(),
              errorMessage: ""
            })
          }));
        }
      });
    }
  });

  return normalOperatingDaysRows;
}

/**
 * convert normalOperatingDaysRows to normalOperatingDays
 * @param {Immutable.List} normalOperatingDaysRows
 * @returns {Immutable.Map}
 */
export function getNormalOperatingDays(normalOperatingDaysRows) {
  const normalOperatingDays = {
    Monday: null,
    Tuesday: null,
    Wednesday: null,
    Thursday: null,
    Friday: null,
    Saturday: null,
    Sunday: null
  };

  normalOperatingDaysRows.forEach((row) => {
    row.get("days").forEach((day) => {
      const { start, end } = getStartEndTimes(row);

      if (normalOperatingDays[day]) {
        normalOperatingDays[day].push({ start, end });
      } else {
        normalOperatingDays[day] = [{ start, end }];
      }
    });
  });

  return Immutable.fromJS(normalOperatingDays);
}

/**
 * convert specialOperatingDays to specialOperatingDaysRows
 * @param {Immutable.Map} specialOperatingDays
 * @returns {Immutable.List}
 */
export function getSpecialOperatingDaysRows(specialOperatingDays) {
  const specialOperatingDaysRows = [];

  if (specialOperatingDays) {
    specialOperatingDays.forEach((timeRange, date) => {
      const { allDay, start, end } = getTimeInfo(
        timeRange.get("start"),
        timeRange.get("end")
      );

      specialOperatingDaysRows.push({
        date,
        start,
        end,
        allDay
      });
    });
  }

  return Immutable.fromJS(specialOperatingDaysRows);
}

/**
 * convert specialOperatingDaysRows to specialOperatingDays
 * @param {Immutable.List} specialOperatingDaysRows
 * @returns {Immutable.Map}
 */
export function getSpecialOperatingDays(specialOperatingDaysRows) {
  const specialOperatingDays = {};

  specialOperatingDaysRows.forEach((row) => {
    const { start, end } = getStartEndTimes(row);
    const date = row.get("date");

    specialOperatingDays[date] = { start, end };
  });

  return Immutable.fromJS(specialOperatingDays);
}

/**
 *
 * @param {*} schedule
 */
export function convertSchedule(schedule) {
  let newSchedule = schedule;

  newSchedule = newSchedule.set("normalOperatingDaysRows", getNormalOperatingDaysRows(newSchedule.get("normalOperatingDays")));
  newSchedule = newSchedule.set("specialOperatingDaysRows", getSpecialOperatingDaysRows(newSchedule.get("specialOperatingDays")));

  return newSchedule;
}

/**
 * Convert a single schedule from the settins page to payload format
 * @param {*} schedule
 */
export function convertScheduleToPaylod(schedule) {
  let newSchedule = schedule;

  newSchedule = newSchedule.set("normalOperatingDays", getNormalOperatingDays(newSchedule.get("normalOperatingDaysRows")));
  newSchedule = newSchedule.set("specialOperatingDays", getSpecialOperatingDays(newSchedule.get("specialOperatingDaysRows")));

  return newSchedule;
}

/**
 * Convert a list of schedules from the settings page to payload format.
 * @param {Immutable.List} schedules
 */
export function convertSchedulesToPayload(schedules) {
  return schedules.map(schedule => convertScheduleToPaylod(schedule));
}
